// Third-Party
import React, { Component } from 'react';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

// Component
import { Button } from '@capintel/component-lib/button';
import CommentContent from './CommentContent';

// Utils
import { FormattedMessage } from '../../../utils/i18n';
import { timeSince } from '../../../utils/time';

type Props = {
  dotNumber: number;
  value?: string;
  isPosted?: boolean;
  datePosted?: Date;
  onClickOutside: () => void;
  onCommentChange: (comment: string) => void;
  postComment: (comment: string) => void;
  deleteComment: () => void;
  actionsDisabled?: boolean;
  userInfos?: {
    imgUrl?: string;
    name?: string;
  };
};

type State = {
  comment?: string;
  isEditing: boolean;
  dotTop?: number;
  dotLeft?: number;
  dotWidth?: number;
  anchorTop?: number;
  anchorWidth?: number;
};

// Dont want to use getBoundingClient on Dot because it takes into account the transform which messes the coordinates
const dotWidth = 24;

class Comment extends Component<Props, State> {
  private commentContainer = React.createRef<HTMLDivElement>();

  readonly state: State = {
    comment: this.props.value,
    isEditing: !this.props.isPosted,
  };

  setElementCoordinates = throttle(() => {
    const { dotNumber } = this.props;

    const dot = document.getElementsByClassName(`dot-${dotNumber}`)[0] as HTMLElement;
    const anchor = document.getElementsByClassName(`anchor-${dotNumber}`)[0] as HTMLElement;
    const comment = document.getElementsByClassName(`comment-${dotNumber}`)[0] as HTMLElement;

    if (dot && anchor && comment) {
      const { offsetTop: dotTop, offsetLeft: dotLeft } = dot;

      const { offsetTop: anchorTop } = anchor;
      const anchorWidth = anchor.getBoundingClientRect().width;

      this.setState({ dotTop, dotLeft, anchorTop, anchorWidth });
    }
  }, 30);

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    window.addEventListener('resize', this.setElementCoordinates);

    this.setElementCoordinates();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { dotNumber } = this.props;
    const { isEditing } = this.state;

    const dot = document.getElementsByClassName(`dot-${dotNumber}`)[0] as HTMLElement;
    const anchor = document.getElementsByClassName(`anchor-${dotNumber}`)[0] as HTMLElement;
    const comment = document.getElementsByClassName(`comment-${dotNumber}`)[0] as HTMLElement;

    if (dot && anchor && comment) {
      const { offsetTop: dotTop, offsetLeft: dotLeft } = dot;

      const { offsetTop: anchorTop } = anchor;
      const anchorWidth = anchor.getBoundingClientRect().width;

      if (dotLeft !== prevState.dotLeft || isEditing !== prevState.isEditing) {
        this.setState({ dotTop, dotLeft, anchorTop, anchorWidth });
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    window.removeEventListener('resize', this.setElementCoordinates);
  }

  getCommentRef = () => (this.commentContainer && this.commentContainer.current ? this.commentContainer.current : null);

  onChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    this.setState({ comment: event.currentTarget.value });
  };

  handleClickOutside = (event: Event) => {
    const { isPosted } = this.props;
    const { comment } = this.state;

    if (!(event.target instanceof Node)) {
      return;
    }
    const commentRef = this.getCommentRef();

    if (commentRef && !commentRef.contains(event.target)) {
      this.props.onClickOutside();

      // If users are creating a comment we will store the values in redux in case they come back to it
      if (comment && !isPosted) {
        this.props.onCommentChange(comment);
      }
    }
  };

  editComment = () => {
    this.setState({ isEditing: true });
  };

  postComment = () => {
    const { comment } = this.state;

    if (!comment) {
      return;
    }

    this.setState({ isEditing: false });
    this.props.postComment(comment);
  };

  updateComment = () => {
    const { comment } = this.state;

    if (!comment) {
      return;
    }

    this.setState({ isEditing: false });
    this.props.onCommentChange(comment);
  };

  renderActionButton = () => {
    const { isPosted } = this.props;
    const { comment, isEditing } = this.state;

    if (!isPosted) {
      return (
        <Button variant="primary" disabled={!comment} size="small" onClick={this.postComment}>
          <FormattedMessage id="MarkupTools.Comment.Post" />
        </Button>
      );
    }

    if (isEditing) {
      return (
        <Button variant="primary" disabled={!comment} size="small" onClick={this.updateComment}>
          <FormattedMessage id="MarkupTools.Comment.Update" />
        </Button>
      );
    }

    return null;
  };

  calcYPosition = (dotTop?: number, anchorTop?: number) => {
    if (dotTop && anchorTop) {
      // Anchor top offset is based on the absolute position of the comment popup which initially starts at top of card
      // Need to add half height/width of dot to center it properly
      return dotTop - anchorTop + dotWidth / 2;
    }
  };

  calcXPosition = (dotLeft?: number, anchorWidth?: number) => {
    const dotToAnchorSpacing = 3;

    if (dotLeft && anchorWidth) {
      return dotLeft + dotWidth + anchorWidth / 2 + dotToAnchorSpacing;
    }
  };

  render() {
    const { dotNumber, datePosted, isPosted, actionsDisabled, userInfos } = this.props;
    const { comment, isEditing, dotTop, anchorTop, dotLeft, anchorWidth } = this.state;

    const yPosition = this.calcYPosition(dotTop, anchorTop);
    const xPosition = this.calcXPosition(dotLeft, anchorWidth);
    const commentPosition = `translate(${xPosition}px, ${yPosition}px)`;

    return (
      <div
        ref={this.commentContainer}
        className={classNames(`comment comment-${dotNumber}`)}
        style={{ transform: commentPosition }}
        onClick={e => e.stopPropagation()}
      >
        <div className={`anchor anchor-${dotNumber}`} />

        <CommentContent
          comment={comment}
          isEditing={isEditing}
          isPosted={isPosted}
          timestamp={datePosted && timeSince(datePosted)}
          userInfos={userInfos}
          onChange={this.onChange}
          editComment={this.editComment}
          deleteComment={this.props.deleteComment}
          actionsDisabled={actionsDisabled}
        />

        {this.renderActionButton()}
      </div>
    );
  }
}

export default Comment;
